import React from 'react';
import type { ComparisonTab } from '@app-types/comparison';
import './ComparisonTabs.less';

export interface ComparisonTabsProps {
  /** Tab items rendered in order; visual active state driven by `active`. */
  tabs: ComparisonTab[];
  /** Formatted update timestamp text shown on the right (e.g. `2026/03/18 更新`). */
  updatedAt: string;
  /** Optional Figma node id, echoed as `data-node-id` on the root for QA. */
  nodeId?: string;
  /**
   * Optional click handler. When provided, receives the tab's `key` on click.
   * If omitted, the tab is display-only (backward compatible with the
   * consensus-only screen).
   */
  onTabClick?: (tabKey: string) => void;
}

/**
 * Static comparison tabs + update-time row.
 *
 * Figma references:
 * - Desktop: `3203:14212`, 626 × 25 (single row).
 * - Mobile: `3203:14222`, 343 × 58 (wraps into two rows).
 *
 * Tabs are display-only when `onTabClick` is not provided. When provided,
 * clicking a tab calls `onTabClick(tabKey)` and the parent should re-render
 * with the tab's `active` flag flipped.
 */
const ComparisonTabs: React.FC<ComparisonTabsProps> = ({ tabs, updatedAt, nodeId, onTabClick }) => {
  return (
    <div className="comparison-tabs" data-node-id={nodeId}>
      <ul className="comparison-tabs__list">
        {tabs.map((tab) => (
          <li key={tab.key}>
            <button
              type="button"
              className={`comparison-tabs__tab${tab.active ? ' is-active' : ''}`}
              aria-pressed={!!tab.active}
              onClick={onTabClick ? () => onTabClick(tab.key) : undefined}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>
      <span className="comparison-tabs__updated">{updatedAt}</span>
    </div>
  );
};

export default ComparisonTabs;
